import { CommonModule } from '@angular/common';
import {
	AfterViewInit,
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	input,
	OnDestroy,
	output,
	viewChild,
	ChangeDetectionStrategy,
} from '@angular/core';
import { LineChartConfig } from '@framework/analytics/models/chart-config.model';
import { LineChartData } from '@framework/analytics/models/chart-data.model';
import { ThemeConfig } from '@framework/analytics/models/theme.model';
import { DataService } from '@framework/analytics/services/data.service';
import {
	highchartsPaletteForThemeMode,
	pinHighchartsPaletteAndTooltipForThemeMode,
	pinHighchartsPaletteForThemeMode,
} from '@framework/analytics/utils/highcharts-palette-for-theme.util';
import {
	explicitChartTooltipHtmlTheme,
	resolveChartThemeMode,
} from '@framework/analytics/utils/resolve-chart-theme-mode.util';
import { PaletteService } from '@framework/analytics/services/palette.service';
import { parseChartVisualizationInput } from '../../../utils/parse-chart-visualization-input.util';
import {
	resolveHighchartsCssColors,
	createHighchartsResizeObserver,
	reflowHighchartsToContainer,
	scheduleHighchartsReflowAfterLayout,
} from '../../../utils/resolve-highcharts-css-colors.util';
import {
	applyLegendLayoutToChartOptions,
	isLegendLayoutDisabled,
} from '../../../utils/chart-legend-scale.util';
import { ChartRenderCoordinator } from '../../../utils/chart-render-coordinator.util';
import {
	ThemeMode,
	ThemeService,
} from '@framework/analytics/services/theme.service';
import Highcharts from '../highcharts.config';
import {
	createLineChartTooltipFormatter,
	lineChartDefaults,
} from './jepto-line-chart.defaults';
import {
	applyLineChartColorPolicy,
	getLineChartColorPolicy,
	resolveLineChartPaletteColors,
} from './line-chart-color-policy';
import {
	coerceLineChartStringX,
	LINE_CHART_MIN_LIKELY_TIMESTAMP_MS,
} from './line-chart-string-x.util';

/**
 * Data point structure for line chart series
 */
interface LineChartDataPoint {
	x: number | string;
	y: number;
}

/**
 * Series structure for line chart
 */
interface LineChartSeries {
	name: string;
	data: LineChartDataPoint[];
	color?: string;
	label?: string;
	aggregationValue?: number;
	comparisonAggregationValue?: number;
	comparisonData?: any[];
	custom?: any;
}

/**
 * Click event structure emitted by the chart
 */
interface ChartClickEvent {
	point: {
		x: number | string;
		y: number;
		series: string;
	};
	chart: {
		type: string;
		id?: string;
	};
}

/**
 * Line Chart Component
 *
 * Renders line charts using Highcharts with full signal-based reactivity.
 * Automatically updates when config, data, or theme changes.
 */
@Component({
	selector: 'jepto-line-chart',
	templateUrl: './jepto-line-chart.component.html',
	styleUrls: ['./jepto-line-chart.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [CommonModule],
})
export class JeptoLineChartComponent implements AfterViewInit, OnDestroy {
	// ═══════════════════════════════════════════════════════════════════════════
	// SIGNAL INPUTS
	// ═══════════════════════════════════════════════════════════════════════════

	cardId = input<string>();
	config = input<LineChartConfig>();
	data = input<LineChartData>();
	themeMode = input<ThemeMode>('auto');
	palette = input<string>('pal_jepto');
	/** Report theme palette id when card inherits report theme palette. */
	reportThemePaletteId = input<string | undefined>(undefined);
	themeConfig = input<ThemeConfig>();
	height = input<string>('100%');
	width = input<string>('100%');
	showLegend = input<boolean>(true);
	enableTooltip = input<boolean>(true);

	// ═══════════════════════════════════════════════════════════════════════════
	// OUTPUTS
	// ═══════════════════════════════════════════════════════════════════════════

	chartClick = output<ChartClickEvent>();
	seriesClick = output<ChartClickEvent>();

	// ═══════════════════════════════════════════════════════════════════════════
	// VIEW QUERIES
	// ═══════════════════════════════════════════════════════════════════════════

	private chartContainer = viewChild.required<ElementRef>('chartContainer');

	// ═══════════════════════════════════════════════════════════════════════════
	// SERVICES
	// ═══════════════════════════════════════════════════════════════════════════

	private themeService = inject(ThemeService);
	private paletteService = inject(PaletteService);
	private dataService = inject(DataService, { optional: true });

	// ═══════════════════════════════════════════════════════════════════════════
	// INTERNAL STATE
	// ═══════════════════════════════════════════════════════════════════════════

	private chart?: Highcharts.Chart;
	private resizeObserver?: ResizeObserver;
	private initialized = false;
	private renderCoordinator = new ChartRenderCoordinator();

	/**
	 * Returns true when a line-like series would not render as a visible line.
	 */
	private isNonRenderableLineSeries(
		series: Highcharts.SeriesOptionsType | undefined,
		globalSeriesOpts?: {
			lineWidth?: number;
			opacity?: number;
			visible?: boolean;
		}
	): boolean {
		const s = (series || {}) as Highcharts.SeriesLineOptions & {
			opacity?: number;
			visible?: boolean;
		};
		const data = Array.isArray(s.data) ? s.data : [];
		if (data.length === 0) return true;
		const effectiveVisible = s.visible ?? globalSeriesOpts?.visible;
		const effectiveOpacity = s.opacity ?? globalSeriesOpts?.opacity;
		const effectiveLineWidth = s.lineWidth ?? globalSeriesOpts?.lineWidth;
		if (effectiveVisible === false) return true;
		if (typeof effectiveOpacity === 'number' && effectiveOpacity <= 0)
			return true;
		if (typeof effectiveLineWidth === 'number' && effectiveLineWidth <= 0)
			return true;
		return false;
	}

	// ═══════════════════════════════════════════════════════════════════════════
	// COMPUTED SIGNALS
	// ═══════════════════════════════════════════════════════════════════════════

	/** Effective data - direct input takes priority over service */
	private effectiveData = computed(() => {
		const direct = this.data();
		if (direct) return direct;

		const id = this.cardId();
		if (id && this.dataService) {
			const serviceData = this.dataService.cardDataSignal(id)();
			if (serviceData?.chartType === 'line') {
				return serviceData as unknown as LineChartData;
			}
		}
		return null;
	});

	/** Colors resolved from input themeMode + selected palette/custom overrides. */
	private paletteColors = computed(() => {
		const cfg = this.config();
		const resolvedThemeForPalette = resolveChartThemeMode(
			this.themeMode(),
			this.themeService.getActiveThemeModeSignal()()
		);
		const palInput = this.palette();
		return resolveLineChartPaletteColors({
			configPalette: cfg?.palette,
			paletteId: palInput,
			themeMode: resolvedThemeForPalette,
			reportThemePaletteId: this.reportThemePaletteId(),
			getPaletteById: paletteId =>
				this.paletteService.getPaletteById(paletteId),
			getColorsForPalette: paletteId =>
				this.paletteService.getColorPalette(paletteId),
		});
	});

	/** Transform data to Highcharts series format */
	private chartData = computed(() => {
		const sourceData = this.effectiveData();
		if (!sourceData?.data?.series) return [];

		return sourceData.data.series.map(series => {
			const transformedSeries: LineChartSeries = {
				name: series.name,
				data: series.data.map((point, index) => {
					let x: number | string, y: number;

					if (Array.isArray(point)) {
						x = point[0];
						y = point[1];
					} else if (typeof point === 'object' && point !== null) {
						x = (point as any).x;
						y = (point as any).y;
					} else {
						return { x: index, y: 0 };
					}

					// Ensure x has a usable value. Some API payloads return null x values.
					if (x === null || x === undefined || x === '') {
						x = index;
					}

					if (typeof x === 'string') {
						x = coerceLineChartStringX(x);
					}

					return { x, y: Number(y) || 0 };
				}),
				color: (series as any).color,
				label: (series as any).label,
				aggregationValue: (series as any).aggregationValue,
				comparisonAggregationValue: (series as any)
					.comparisonAggregationValue,
				comparisonData: (series as any).comparisonData,
				custom: (series as any).custom || {},
			};

			return transformedSeries;
		});
	});

	/**
	 * Infer whether x-axis should be treated as datetime or category.
	 * Some payloads send small numeric x values (e.g. 2,4,6) which are not unix ms
	 * timestamps; treating those as datetime collapses labels to Jan 1.
	 */
	private xAxisType = computed<'datetime' | 'category'>(() => {
		const data = this.chartData();
		const xValues = data.flatMap(series =>
			series.data.map(point => point.x)
		);
		if (xValues.length === 0) {
			return 'datetime';
		}

		const numericX = xValues.filter(
			(value): value is number =>
				typeof value === 'number' && Number.isFinite(value)
		);
		// Mixed string/categorical/non-finite X with timestamp-like numbers must stay
		// categorical; inferring datetime would coerce unparsable strings to bogus X.
		if (numericX.length !== xValues.length) {
			return 'category';
		}
		if (numericX.length === 0) {
			return 'category';
		}

		const looksLikeDatetime = numericX.some(
			value => value >= LINE_CHART_MIN_LIKELY_TIMESTAMP_MS
		);
		return looksLikeDatetime ? 'datetime' : 'category';
	});

	/** Chart options - recomputed when any dependency changes */
	private chartOptions = computed(() => {
		const baseOptions = { ...lineChartDefaults };
		const cfg = this.config();
		// AppSync AWSJSON may deserialize as a string at runtime; model types it as object only.
		const rawViz: unknown = cfg?.visualization;
		const visualizationResolved =
			rawViz == null || rawViz === ''
				? undefined
				: typeof rawViz === 'string'
					? parseChartVisualizationInput(rawViz)
					: rawViz;
		const visualizationForMerge: Highcharts.Options | undefined =
			visualizationResolved &&
			typeof visualizationResolved === 'object' &&
			!Array.isArray(visualizationResolved)
				? (visualizationResolved as Highcharts.Options)
				: undefined;
		const vizColors = visualizationForMerge?.colors;
		const colorPolicy = getLineChartColorPolicy({
			palette: cfg?.palette,
			visualization: visualizationForMerge as
				| { colors?: unknown }
				| undefined,
		});
		const paletteColorsArr = this.paletteColors();
		const colors =
			colorPolicy.useVisualizationColors &&
			Array.isArray(vizColors) &&
			vizColors.length > 0
				? vizColors
				: paletteColorsArr;
		const stringColors = colors.filter(
			(color): color is string => typeof color === 'string'
		);
		const seriesData = this.transformDataToSeries();
		const legend = this.showLegend();
		const tooltip = this.enableTooltip();
		const id = this.cardId();
		const xAxisType = this.xAxisType();

		// Track theme mode and config so charts re-render when report theme CSS vars apply.
		this.themeConfig();
		const themeModeInput = this.themeMode();
		const pinnedHtmlTheme = explicitChartTooltipHtmlTheme(themeModeInput);
		const resolvedThemeMode = resolveChartThemeMode(
			themeModeInput,
			this.themeService.getActiveThemeModeSignal()()
		);
		this.themeService.themeIdDisplaySignal();

		const componentOptions: Partial<Highcharts.Options> = {
			...highchartsPaletteForThemeMode(resolvedThemeMode),
			colors: colors.length > 0 ? colors : undefined,
			chart: {
				...baseOptions.chart,
				backgroundColor: 'var(--chart-background, transparent)',
			},
			title: {
				text: cfg?.title || '',
				style: { color: 'var(--chart-text-color, inherit)' },
			},
			subtitle: {
				text: cfg?.subtitle || '',
				style: { color: 'var(--chart-text-color, inherit)' },
			},
			xAxis: {
				...baseOptions.xAxis,
				type: xAxisType,
				title: {
					text: '',
					style: { color: 'var(--chart-text-color, inherit)' },
				},
			},
			yAxis: {
				...baseOptions.yAxis,
				title: {
					text: '',
					style: { color: 'var(--chart-text-color, inherit)' },
				},
			},
			legend: {
				...baseOptions.legend,
				enabled: legend,
			},
			tooltip: {
				...baseOptions.tooltip,
				enabled: tooltip,
				formatter: createLineChartTooltipFormatter(
					xAxisType,
					pinnedHtmlTheme
				),
			},
			plotOptions: {
				...baseOptions.plotOptions,
				line: {
					...baseOptions.plotOptions?.line,
					point: {
						events: {
							click: (event: any) => {
								this.chartClick.emit({
									point: {
										x: event.point.x,
										y: event.point.y,
										series: event.point.series.name,
									},
									chart: { type: 'line', id },
								});
							},
						},
					},
				},
				series: {
					...baseOptions.plotOptions?.series,
					events: {
						click: (event: any) => {
							this.seriesClick.emit({
								point: {
									x: event.point?.x || 0,
									y: event.point?.y || 0,
									series: event.target.name,
								},
								chart: { type: 'line', id },
							});
						},
					},
				},
			},
			series: seriesData,
		};

		// Deep merge: defaults -> component options
		let merged = Highcharts.merge(baseOptions, componentOptions);
		merged.series = seriesData;

		// Apply visualization overrides from config (AWSJSON may arrive as a string)
		if (visualizationForMerge) {
			merged = Highcharts.merge(merged, visualizationForMerge);
			// Apply visualization.series by index so type/marker/lineWidth etc. apply without losing series data
			const vizSeries = visualizationForMerge.series;
			if (
				Array.isArray(vizSeries) &&
				vizSeries.length > 0 &&
				seriesData.length > 0
			) {
				merged.series = seriesData.map((s, i) => {
					const viz = vizSeries[i] || {};
					const item = Highcharts.merge(
						s,
						viz
					) as Highcharts.SeriesOptionsType;
					// Runtime API data is the source of truth for plotted points. Keep it even when
					// visualization JSON contains stale/empty `series[i].data`.
					(item as Highcharts.SeriesLineOptions).data = (
						s as Highcharts.SeriesLineOptions
					).data;
					if (
						viz &&
						(viz as Highcharts.SeriesOptionsType).type !== undefined
					) {
						(item as Highcharts.SeriesLineOptions).type = (
							viz as Highcharts.SeriesLineOptions
						).type;
					}
					return item;
				}) as Highcharts.SeriesOptionsType[];
			} else {
				// Custom-tab JSON is seeded with `series: []` from defaults; merging must not wipe data-driven series.
				merged.series = seriesData;
			}
		}

		// Apply color precedence policy after all config merges to prevent persisted
		// visualization overrides from forcing stale colors on initial load.
		merged = applyLineChartColorPolicy(
			merged as { colors?: unknown; series?: unknown[] },
			colorPolicy,
			stringColors
		) as Highcharts.Options;

		// Recovery guard: if data exists but every line series is non-renderable due to
		// visualization overrides (visible=false/opacity=0/lineWidth=0), restore sane defaults.
		const mergedSeries = Array.isArray(merged.series)
			? (merged.series as Highcharts.SeriesOptionsType[])
			: [];
		const hasSourceData = seriesData.some(s => {
			const data = (s as Highcharts.SeriesLineOptions).data;
			return Array.isArray(data) && data.length > 0;
		});
		const globalSeriesOpts = (merged.plotOptions?.series || {}) as {
			lineWidth?: number;
			opacity?: number;
			visible?: boolean;
		};
		if (
			hasSourceData &&
			mergedSeries.length > 0 &&
			mergedSeries.every(s =>
				this.isNonRenderableLineSeries(s, globalSeriesOpts)
			)
		) {
			const mergedPlotOptions = (merged.plotOptions ||
				{}) as Highcharts.PlotOptions;
			const mergedPlotSeries = (mergedPlotOptions.series ||
				{}) as Highcharts.PlotSeriesOptions;
			merged.plotOptions = {
				...mergedPlotOptions,
				series: {
					...mergedPlotSeries,
					visible: true,
					opacity:
						typeof (mergedPlotSeries as { opacity?: number })
							.opacity === 'number' &&
						(mergedPlotSeries as { opacity?: number }).opacity <= 0
							? 1
							: (mergedPlotSeries as { opacity?: number })
									.opacity,
					lineWidth:
						typeof mergedPlotSeries.lineWidth === 'number' &&
						mergedPlotSeries.lineWidth <= 0
							? 2
							: mergedPlotSeries.lineWidth,
				},
			};
			merged.series = mergedSeries.map(s => {
				const out = { ...(s as Highcharts.SeriesLineOptions) };
				out.visible = true;
				if (typeof out.opacity === 'number' && out.opacity <= 0) {
					out.opacity = 1;
				}
				if (typeof out.lineWidth === 'number' && out.lineWidth <= 0) {
					out.lineWidth = 2;
				}
				return out as Highcharts.SeriesOptionsType;
			});
		}

		if (isLegendLayoutDisabled(merged)) {
			merged.legend = {
				...(merged.legend as Highcharts.LegendOptions),
				useHTML: false,
				alignColumns: false,
				labelFormatter: function () {
					return this.name;
				},
			};
		}

		if (pinnedHtmlTheme) {
			return pinHighchartsPaletteAndTooltipForThemeMode(
				merged,
				resolvedThemeMode,
				createLineChartTooltipFormatter(xAxisType, pinnedHtmlTheme)
			);
		}
		return pinHighchartsPaletteForThemeMode(merged, resolvedThemeMode);
	});

	// ═══════════════════════════════════════════════════════════════════════════
	// CONSTRUCTOR & EFFECTS
	// ═══════════════════════════════════════════════════════════════════════════

	constructor() {
		// Update chart when options change (coalesced via rAF — JEP-10271).
		effect(() => {
			const options = this.chartOptions();
			const data = this.chartData();

			if (this.initialized && this.chart && data.length > 0) {
				const chart = this.chart;
				this.renderCoordinator.schedule(() => {
					chart.destroy();
					this.renderChart(options);
				});
			}
		});
	}

	// ═══════════════════════════════════════════════════════════════════════════
	// LIFECYCLE
	// ═══════════════════════════════════════════════════════════════════════════

	ngAfterViewInit(): void {
		this.initializeChart();
		this.setupResizeObserver();
		this.initialized = true;
	}

	ngOnDestroy(): void {
		this.renderCoordinator.cancel();
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}
		if (this.chart) {
			this.chart.destroy();
		}
	}

	// ═══════════════════════════════════════════════════════════════════════════
	// PRIVATE METHODS
	// ═══════════════════════════════════════════════════════════════════════════

	private renderChart(options: Highcharts.Options): void {
		const container = this.chartContainer().nativeElement;
		const resolved = resolveHighchartsCssColors(
			applyLegendLayoutToChartOptions(options, container),
			container
		);
		this.chart = Highcharts.chart(container, resolved);
		reflowHighchartsToContainer(this.chart, container);
		scheduleHighchartsReflowAfterLayout(() => this.chart, container);
	}

	private initializeChart(): void {
		const container = this.chartContainer();
		if (container) {
			this.renderChart(this.chartOptions());
		}
	}

	private setupResizeObserver(): void {
		this.resizeObserver?.disconnect();
		this.resizeObserver = createHighchartsResizeObserver(
			() => this.chart,
			this.chartContainer().nativeElement
		);
	}

	private transformDataToSeries(): Highcharts.SeriesOptionsType[] {
		const inputData = this.chartData();
		if (!inputData || inputData.length === 0) return [];
		const xAxisType = this.xAxisType();

		return inputData.map(series => {
			if (!series.data || series.data.length === 0) {
				return {
					type: 'line' as const,
					name: series.label || series.name,
					data: [],
					aggregationValue: series.aggregationValue,
					comparisonAggregationValue:
						series.comparisonAggregationValue,
					comparisonData: series.comparisonData,
					custom: series.custom,
				} as any;
			}

			const seriesData = series.data.map((point, index) => {
				if (typeof point.x === 'string') {
					const ts = coerceLineChartStringX(point.x);
					return xAxisType === 'datetime'
						? [
								typeof ts === 'number' && Number.isFinite(ts)
									? ts
									: point.x || `Point ${index + 1}`,
								point.y,
							]
						: [point.x || `Point ${index + 1}`, point.y];
				}
				return xAxisType === 'datetime'
					? [point.x, point.y]
					: [
							point.x === null || point.x === undefined
								? `Point ${index + 1}`
								: String(point.x),
							point.y,
						];
			});

			// Highcharts line series requires ascending X values.
			const shouldSortByX =
				xAxisType === 'datetime' &&
				seriesData.every(
					p =>
						typeof p[0] === 'number' &&
						Number.isFinite(p[0] as number)
				);
			const normalizedSeriesData = shouldSortByX
				? [...seriesData].sort(
						(a, b) => (a[0] as number) - (b[0] as number)
					)
				: seriesData;

			return {
				type: 'line' as const,
				name: series.label || series.name,
				data: normalizedSeriesData,
				aggregationValue: series.aggregationValue,
				comparisonAggregationValue: series.comparisonAggregationValue,
				comparisonData: series.comparisonData,
				custom: series.custom,
			} as any;
		});
	}
}
